import React, { useEffect, useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const LocationDisplay = ({ onLocationCapture, isCapturing }) => {
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accuracy, setAccuracy] = useState(null);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const reverseGeocode = async (lat, lng) => {
    try {
      // Using OpenStreetMap Nominatim API (free, no key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            'User-Agent': 'LaborPro-ERP/1.0'
          }
        }
      );
      const data = await response.json();
      if (data && data.display_name) {
        return data.display_name;
      }
    } catch (err) {
      console.error('Reverse geocoding error:', err);
    }
    return `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
  };

  const getCurrentLocation = () => {
    setIsLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      setIsLoading(false);
      return;
    }

    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position?.coords;
        setLocation({ latitude, longitude });
        setAccuracy(Math.round(accuracy));

        // Get address from reverse geocoding
        const addr = await reverseGeocode(latitude, longitude);
        setAddress(addr);

        onLocationCapture({
          latitude,
          longitude,
          accuracy: Math.round(accuracy),
          address: addr
        });

        setIsLoading(false);
      },
      (err) => {
        setIsLoading(false);
        if (err?.code === 1) {
          setError('Location access denied. Please allow location permissions in your browser settings.');
        } else if (err?.code === 2) {
          setError('Unable to determine your location. Please check your device settings.');
        } else if (err?.code === 3) {
          setError('Location request timed out. Please try again.');
        } else {
          setError('An error occurred while getting your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  if (error) {
    return (
      <div className="w-full bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2">
        <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
            <Icon name="MapPinOff" size={20} className="md:w-6 md:h-6 lg:w-7 lg:h-7 text-error" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">Location Access Required</h3>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground">{error}</p>
          </div>
        </div>
        <Button
          variant="default"
          onClick={getCurrentLocation}
          iconName="RefreshCw"
          iconPosition="left"
          fullWidth
        >
          Retry Location
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <div className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1">Getting your location...</h3>
            <p className="text-sm md:text-base text-muted-foreground">Please wait while we determine your position</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-card rounded-xl overflow-hidden shadow-elevation-2">
      <div className="aspect-video w-full bg-muted relative overflow-hidden">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Current Location"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${location?.latitude},${location?.longitude}&z=15&output=embed`}
          className="border-0"
        />
      </div>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
            <Icon name="MapPin" size={20} className="md:w-6 md:h-6 lg:w-7 lg:h-7 text-success" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">Current Location</h3>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground break-words">{address}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6">
          <div className="bg-muted rounded-lg p-3 md:p-4 lg:p-5">
            <p className="text-xs md:text-sm lg:text-base text-muted-foreground mb-1">Latitude</p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">{location?.latitude?.toFixed(6)}</p>
          </div>
          <div className="bg-muted rounded-lg p-3 md:p-4 lg:p-5">
            <p className="text-xs md:text-sm lg:text-base text-muted-foreground mb-1">Longitude</p>
            <p className="text-sm md:text-base lg:text-lg font-semibold text-foreground">{location?.longitude?.toFixed(6)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 md:gap-3">
            <Icon name="Target" size={16} className="md:w-5 md:h-5 lg:w-6 lg:h-6 text-success" />
            <span className="text-xs md:text-sm lg:text-base text-muted-foreground">Accuracy</span>
          </div>
          <span className="text-sm md:text-base lg:text-lg font-semibold text-foreground">±{accuracy}m</span>
        </div>
      </div>
    </div>
  );
};

export default LocationDisplay;


